import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../contexts/AuthContext';
import { ShellRail, useShellUI } from '../../shell/AppShell';
import MarketNav from './MarketNav';
import MarketLine from './MarketLine';
import MarketAvatar from './MarketAvatar';
import useCountUp from './useCountUp';
import {
  fetchPortfolio,
  cancelOrder,
  setLeaderboardOptIn,
  claimGuestPortfolio,
  fmtChips,
  fmtShares,
  chipWord,
  fmtK,
  fmtPct,
} from './marketApi';
import './market.css';

const RANGES = [
  { key: '1W', days: 7 },
  { key: '1M', days: 30 },
  { key: '3M', days: 90 },
  { key: 'ALL', days: null },
];

const sideChip = (side) =>
  side === 'short' ? <span className="ft-chip outline">Short</span> : <span className="ft-chip dark">Long</span>;

const MarketPortfolio = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useShellUI();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [range, setRange] = useState('1M');
  const [bannerDismissed, setBannerDismissed] = useState(() => {
    try {
      return Boolean(sessionStorage.getItem('mkPortfolioBannerSeen'));
    } catch (e) {
      return false;
    }
  });
  // The hero balance counts up on change instead of snapping on every 60s poll.
  const totalDisplay = useCountUp(data?.account?.total ?? 0, { duration: 700 });

  const load = useCallback(() => {
    fetchPortfolio()
      .then(setData)
      .catch((e) => setError(e.message));
  }, []);

  useEffect(() => {
    load();
    const poll = setInterval(load, 60000); // live-ish: prices retick every 15m in prod
    return () => clearInterval(poll);
  }, [load]);

  // Login funnel: a guest portfolio follows its owner through the door.
  useEffect(() => {
    if (!user) return;
    claimGuestPortfolio()
      .then((r) => {
        if (r.claimed) {
          toast('Portfolio saved to your account');
          load();
        }
      })
      .catch(() => {});
  }, [user, toast, load]);

  // A since-you-last-looked banner only earns its slot once per session, and
  // only once there's something real to say (an actual open book, a real move).
  useEffect(() => {
    if (!data || bannerDismissed) return;
    if (data.positions.length > 0 && Math.abs(data.account.lastCloseChangePct) >= 0.1) {
      try {
        sessionStorage.setItem('mkPortfolioBannerSeen', '1');
      } catch (e) {
        // storage unavailable — banner just won't persist across visits this session
      }
    }
  }, [data, bannerDismissed]);

  const linePoints = useMemo(() => {
    if (!data) return [];
    const days = RANGES.find((r) => r.key === range)?.days;
    if (!days) return data.history;
    return data.history.slice(-Math.max(2, days));
  }, [data, range]);

  if (error) {
    return (
      <div className="ft-view">
        <MarketNav />
        <p className="ft-sub">The market is unreachable. {error}</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="ft-view">
        <MarketNav />
        <div className="ft-loading">Opening your portfolio</div>
      </div>
    );
  }

  const { account, positions, closedPositions, orders, watchlist } = data;
  const empty = positions.length === 0 && orders.length === 0 && closedPositions.length === 0;

  const act = (fn, okMsg) => () =>
    fn()
      .then(() => {
        toast(okMsg);
        load();
      })
      .catch((e) => toast(e.message === 'guest_limit' ? 'Guest limit reached — log in to keep trading' : e.message));

  return (
    <div className="ft-view">
      <Helmet>
        <title>The Market — Fantasy Taco</title>
        <meta
          name="description"
          content="A paper stock market for dynasty players. Buy and short at real KTC value — the market ticks once a night."
        />
      </Helmet>
      <MarketNav />

      <p className="rh-label">
        Portfolio
        <span>paper · prices retick nightly at the close</span>
      </p>
      <div className="rh-bignum">
        {fmtChips(totalDisplay)}
        <small>{chipWord(totalDisplay)}</small>
      </div>
      <div className="rh-delta">
        <span className={account.returnPct >= 0 ? 'mk-up' : 'mk-dn'}>
          {account.returnPct >= 0 ? '▲' : '▼'} {fmtChips(Math.abs(account.total - account.startingCash))} (
          {fmtPct(account.returnPct)})
        </span>{' '}
        <span className="lbl">all time</span>{' '}
        <span className={account.lastCloseChangePct >= 0 ? 'mk-up' : 'mk-dn'}>
          {fmtPct(account.lastCloseChangePct)}
        </span>{' '}
        <span className="lbl">since last close</span>
      </div>

      <div className="rh-chart">
        <MarketLine points={linePoints} baseline={account.startingCash} ariaLabel="Portfolio value" />
      </div>
      <div className="rh-ranges" role="group" aria-label="Chart range">
        {RANGES.map((r) => (
          <button
            key={r.key}
            type="button"
            className={`rh-range${range === r.key ? ' on' : ''}`}
            onClick={() => setRange(r.key)}
          >
            {r.key}
          </button>
        ))}
      </div>
      <div className="rh-power">
        <span>
          Buying power
          {account.reserved > 0 && (
            <span className="sub"> · {fmtChips(account.reserved)} resting in orders</span>
          )}
        </span>
        <span className="mk-mono">{fmtChips(account.cash)}</span>
      </div>

      {!bannerDismissed && positions.length > 0 && Math.abs(account.lastCloseChangePct) >= 0.1 && (
        <div className="ft-card mk-banner">
          <div className="t">
            <b className={account.lastCloseChangePct >= 0 ? 'up' : 'dn'}>
              {account.lastCloseChangePct >= 0 ? 'Up' : 'Down'} {Math.abs(account.lastCloseChangePct).toFixed(1)}%
            </b>{' '}
            since the last close.
          </div>
          <button type="button" className="x" onClick={() => setBannerDismissed(true)} aria-label="Dismiss">
            ×
          </button>
        </div>
      )}

      {account.isGuest && (
        <div className="ft-card mk-gate">
          <div className="g">
            <div className="t">
              Guest portfolio — {account.guestOpensLeft} {account.guestOpensLeft === 1 ? 'trade' : 'trades'} left.
            </div>
            <div className="d">
              Log in with your Sleeper username to keep it.
            </div>
          </div>
          <button type="button" className="ft-btn sm" onClick={() => navigate('/login')}>
            Keep it
          </button>
        </div>
      )}

      {empty && (
        <div className="ft-card" style={{ marginTop: 26, padding: '28px 24px' }}>
          <p style={{ fontSize: 15, fontWeight: 600, margin: '0 0 6px' }}>Nothing on the books.</p>
          <p style={{ fontSize: 13.5, color: 'var(--ft-ink-2)', margin: '0 0 20px' }}>
            {fmtChips(account.cash)} {chipWord(account.cash)} of conviction, unspent. Find a player you have a
            read on.
          </p>
          <div className="mk-how">
            <div className="step">
              <i>01</i>
              <div>
                Buy a player or pick you believe in — you're long, and you profit if his real KTC
                dynasty value rises.
              </div>
            </div>
            <div className="step">
              <i>02</i>
              <div>
                Short one you don't — you profit if it falls instead. Every chip is paper; nothing
                touches your actual roster.
              </div>
            </div>
            <div className="step">
              <i>03</i>
              <div>
                Prices retick once a night, at the close. Market orders fill now; limit orders wait
                for their price.
              </div>
            </div>
          </div>
          <button type="button" className="ft-btn sm" style={{ marginTop: 22 }} onClick={() => navigate('/market/browse')}>
            Browse the market
          </button>
        </div>
      )}

      {orders.length > 0 && (
        <>
          <span className="ft-k mk-seclabel">Resting orders</span>
          {orders.map((o) => (
            <div className="ft-card white mk-pos" key={o.id}>
              <MarketAvatar player={o.player} pick={o.pick} />
              <span className="ft-chip outline">
                {o.action === 'close' ? 'Close' : o.action === 'open_short' ? 'Short' : 'Buy'}
                {o.type === 'limit' ? ' · limit' : ''}
              </span>
              <div className="who">
                <div className="nm">
                  {o.player?.name || o.pick?.label}
                  <span>{o.player ? `${o.player.position} · ${o.player.team}` : 'pick'}</span>
                </div>
                <div className="line">
                  {o.amount ? `${fmtChips(o.amount)} ${chipWord(o.amount)}` : 'full position'}
                  {o.limitPrice ? ` · at ${fmtK(o.limitPrice)}` : ' · at tonight’s close'}
                </div>
              </div>
              <button type="button" className="act" onClick={act(() => cancelOrder(o.id), 'Order cancelled')}>
                Cancel
              </button>
            </div>
          ))}
        </>
      )}

      {positions.length > 0 && (
        <>
          <span className="ft-k mk-seclabel">Positions</span>
          {positions.map((p) => (
            <div className="ft-card mk-pos" key={p.id}>
              <MarketAvatar player={p.player} pick={p.pick} size={34} />
              {sideChip(p.side)}
              <div className="who">
                <div className="nm">
                  {p.player?.name || p.pick?.label}
                  <span>{p.player ? `${p.player.position} · ${p.player.team}` : 'pick'}</span>
                </div>
                <div className="line">
                  {fmtShares(p.shares)} sh · in {fmtK(p.entryValue)} →{' '}
                  {fmtK(p.player ? p.player.value : p.pick?.value)} · since {p.entryDate}
                </div>
              </div>
              <div className="pl">
                <div className={`big ${p.pnl >= 0 ? 'mk-up' : 'mk-dn'}`}>{fmtPct(p.pnlPct)}</div>
                <div className="raw">
                  {p.pnl >= 0 ? '+' : ''}
                  {fmtChips(p.pnl)}
                </div>
              </div>
              <button
                type="button"
                className="act"
                onClick={() =>
                  navigate(
                    p.player
                      ? `/market/p/${p.player.id}`
                      : `/market/pick/${encodeURIComponent(p.pick?.label || '')}`
                  )
                }
              >
                Trade
              </button>
            </div>
          ))}
        </>
      )}

      {closedPositions.length > 0 && (
        <>
          <span className="ft-k mk-seclabel">Settled</span>
          {closedPositions.map((p) => (
            <div className="ft-card white mk-pos" key={p.id}>
              <MarketAvatar player={p.player} pick={p.pick} />
              <span className={`ft-chip ${p.realizedPnl >= 0 ? 'green' : ''}`}>
                {p.realizedPnl >= 0 ? 'Hit' : 'Missed'}
              </span>
              <div className="who">
                <div className="nm">
                  {p.player?.name || p.pick?.label}
                  <span>{p.side === 'short' ? 'covered' : 'sold'} · {p.closedDate}</span>
                </div>
                <div className="line">
                  in {fmtK(p.entryValue)} → out {fmtK(p.closedValue)}
                </div>
              </div>
              <div className="pl">
                <div className={`big ${p.realizedPnl >= 0 ? 'mk-up' : 'mk-dn'}`}>{fmtPct(p.pnlPct)}</div>
                <div className="raw">
                  {p.realizedPnl >= 0 ? '+' : ''}
                  {fmtChips(p.realizedPnl)}
                </div>
              </div>
            </div>
          ))}
        </>
      )}

      <ShellRail>
        <div className="ft-railsec">
          <span className="ft-k">Holdings</span>
          {positions.length === 0 && (
            <p style={{ fontSize: 13, color: 'var(--ft-ink-3)', margin: 0 }}>Nothing held yet.</p>
          )}
          {positions.map((p) => (
            <button
              key={p.id}
              type="button"
              className="rh-hold"
              onClick={() =>
                navigate(
                  p.player
                    ? `/market/p/${p.player.id}`
                    : `/market/pick/${encodeURIComponent(p.pick?.label || '')}`
                )
              }
            >
              <MarketAvatar player={p.player} pick={p.pick} size={26} />
              <div className="n">
                <div className="nm">{p.player?.name || p.pick?.label}</div>
                <div className="sub">
                  {p.side.toUpperCase()} · {fmtShares(p.shares)} SH
                </div>
              </div>
              <div className="r">
                <div className="pv">{fmtChips(p.marketValue)}</div>
                <div className={`pc ${p.pnl >= 0 ? 'mk-up' : 'mk-dn'}`}>{fmtPct(p.pnlPct)}</div>
              </div>
            </button>
          ))}
        </div>
        <div className="ft-railsec">
          <span className="ft-k">Watchlist</span>
          {watchlist.length === 0 && (
            <p style={{ fontSize: 13, color: 'var(--ft-ink-3)', margin: 0 }}>
              Watch a player from his asset page.
            </p>
          )}
          {watchlist.map((w) => (
            <button
              key={w.player?.id || w.pick?.label}
              type="button"
              className="rh-hold"
              onClick={() =>
                navigate(
                  w.player
                    ? `/market/p/${w.player.id}`
                    : `/market/pick/${encodeURIComponent(w.pick?.label || '')}`
                )
              }
            >
              <MarketAvatar player={w.player} pick={w.pick} size={26} />
              <div className="n">
                <div className="nm">{w.player?.name || w.pick?.label}</div>
                <div className="sub">
                  {w.player ? `${w.player.position} · ${w.player.team}` : `matures ${w.pick?.maturity}`}
                </div>
              </div>
              <div className="r">
                <div className="pv">{fmtK(w.player ? w.player.value : w.pick?.value)}</div>
                <div className={`pc ${w.dayChangePct >= 0 ? 'mk-up' : 'mk-dn'}`}>
                  {fmtPct(w.dayChangePct)}
                </div>
              </div>
            </button>
          ))}
        </div>
        <div className="ft-railsec">
          <span className="ft-k">Leaderboard</span>
          <p style={{ fontSize: 13, color: 'var(--ft-ink-2)', margin: '0 0 10px', lineHeight: 1.5 }}>
            {account.leaderboardOptIn
              ? 'Your name and return are visible on the board.'
              : 'Private by default — sealed until you add it.'}
          </p>
          <button
            type="button"
            className="ft-btn sm"
            onClick={act(
              () => setLeaderboardOptIn(!account.leaderboardOptIn),
              account.leaderboardOptIn ? 'Off the board' : 'On the board'
            )}
          >
            {account.leaderboardOptIn ? 'Remove me from the leaderboard' : 'Add me to the leaderboard'}
          </button>
        </div>
        <div className="ft-railsec">
          <span className="ft-k">The close</span>
          <p style={{ fontSize: 13, color: 'var(--ft-ink-2)', margin: 0, lineHeight: 1.5 }}>
            Prices retick nightly at 11:55 PM ET, after the KTC sync. Market orders fill instantly;
            resting limit orders fill at the close the night they touch.
          </p>
        </div>
      </ShellRail>
    </div>
  );
};

export default MarketPortfolio;
